/**
 * Top-level menu identifiers used by both routing and access-control.
 *
 * Phase 6 (2026-05-16): Extended with Timesheet integration keys.
 * All permission strings live here; OPE and Timesheet apps both
 * check these against `user.access_rights` (managed via OPE Settings).
 *
 * Order here drives the order on the Home grid.
 */
export const MENU_KEYS = [
  // OPE original menus (unchanged from v2)
  'ope-entry',
  'report-ope',
  'report-cost',
  'report-summary',
  'report-aqi',
  'report-compare',
  'report-perf',
  'plan-incurred',
  'audit-fees',
  'settings',

  // Phase 8.7 — fine-grained OPE Entry visibility (NEW)
  // Holders see ALL jobs in their own division. Otherwise the OPE Entry
  // job list shows only jobs the user has personally clocked time on
  // (Timesheet entries). Admin bypasses — always sees everything.
  'ope-view-division',

  // Phase 6 — Timesheet integration (NEW)
  'timesheet-entry',         // กรอก/แก้ entry ของตัวเอง
  'timesheet-view-all',      // ดู entry ของทุกคน (read-only, Team Report)
  'timesheet-approve',       // อนุมัติ entry (รอบเดียว) — Division-scoped
  'timesheet-report',        // ดู Reports ทั้งหมด
  'timesheet-admin',         // จัดการ master (Activity/Location/Zone) + menu config
  // Phase 8 — Legacy data migration (revoke after migration window closes)
  'timesheet-legacy-import', // อัพ Excel จาก Timesheet ระบบเก่า (transitional)

  // Phase 9 (2026-05-19) — Performance web (separate Pages project)
  // Gates the entire ope-performance-v3.pages.dev app. Users without
  // this permission see a "ติดต่อ Admin" screen instead of login.
  'performance-access',
] as const;

export type MenuKey = (typeof MENU_KEYS)[number];

export const MENU_LABELS: Record<MenuKey, string> = {
  // OPE
  'ope-entry':       'กรอกข้อมูล OPE',
  'report-ope':      'รายงาน OPE',
  'report-cost':     'Cost Summary / Recovery',
  'report-summary':  'Summary OPE & OT',
  'report-aqi':      'Report (AQI)',
  'report-compare':  'เปรียบเทียบพนักงาน',
  'report-perf':     'ผลการปฏิบัติงาน',
  'plan-incurred':   'Plan vs Incurred (S740)',
  'audit-fees':      'Audit Fee',
  'settings':        'Settings',
  'ope-view-division': 'OPE: เห็น Job ทั้งสายงาน',

  // Phase 6 — Timesheet
  'timesheet-entry':     'Timesheet (กรอก)',
  'timesheet-view-all':  'Report Timesheet',
  'timesheet-approve':   'อนุมัติ Timesheet',
  'timesheet-report':    'Summary Timesheet',
  'timesheet-admin':     'ตั้งค่า Timesheet',
  'timesheet-legacy-import': 'อัพ Timesheet ระบบเก่า (Migration)',

  // Phase 9 — Performance web
  'performance-access':  'เข้าใช้งานเว็บ Performance',
};

export const ALL_MENU_KEYS: readonly MenuKey[] = MENU_KEYS;

/**
 * Helper: keys grouped by domain for Settings → Access Rights UI.
 * Lets the admin see "OPE permissions" and "Timesheet permissions" as
 * separate sections instead of one long flat list.
 */
export const MENU_GROUPS = {
  ope: [
    'ope-entry',
    'report-ope',
    'report-cost',
    'report-summary',
    'report-aqi',
    'report-compare',
    'report-perf',
    'plan-incurred',
    'audit-fees',
    'settings',
    'ope-view-division',
  ],
  timesheet: [
    'timesheet-entry',
    'timesheet-view-all',
    'timesheet-approve',
    'timesheet-report',
    'timesheet-admin',
    'timesheet-legacy-import',
  ],
  performance: [
    'performance-access',
    // Note: report-perf and report-compare are listed under `ope` above
    // because the OPE web also exposes them; the Performance web reuses
    // the same permission keys.
  ],
} as const satisfies Record<string, readonly MenuKey[]>;
